import { Splide, SplideSlide } from '@splidejs/react-splide';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo, useRef } from 'react';
import { isEmpty } from 'validate.js';

import Badge from 'dpl/common/design_system/Badge';
import SkeletonBlock from 'dpl/common/design_system/SkeletonBlock';
import { makeSequence } from 'dpl/shared/utils';

// Matches the rendered height of a default DSBadge so the skeleton doesn't
// cause layout shift when the async availability data arrives.
const BADGE_SKELETON_HEIGHT = '32px';
const BADGE_SKELETON_WIDTHS = ['140px', '110px'];

export default function BreedAvailability({
  className,
  breedName,
  availablePuppyCount,
  availableAdultDogsCount,
  formattedUpcomingLittersDueDates,
  splideOptions,
  isLoading
}) {
  const splideRef = useRef();

  // The breed leads the row and takes the only color in it. Availability drops
  // to gray behind it: with several breeds in one feed, which breed a card is
  // for outranks how much of it is available.
  const breedDetail = useMemo(
    () =>
      breedName && {
        color: 'primary',
        variant: 'dark',
        description: breedName
      },
    [breedName]
  );

  const details = useMemo(() => {
    const availabilityColor = breedName ? 'gray' : null;

    return [
      breedDetail,
      availablePuppyCount > 0 && {
        color: availabilityColor || 'teal',
        description: `${availablePuppyCount} ${availablePuppyCount === 1 ? 'puppy' : 'puppies'} available`
      },
      ...formattedUpcomingLittersDueDates.map(formattedDueDate => ({
        color: availabilityColor || 'blue',
        description: `Litter due ${formattedDueDate}`
      })),
      availableAdultDogsCount > 0 && {
        color: 'gray',
        description: `${availableAdultDogsCount} adult ${availableAdultDogsCount === 1 ? 'dog' : 'dogs'} available`
      }
    ].filter(Boolean);
  }, [
    breedName,
    breedDetail,
    availablePuppyCount,
    availableAdultDogsCount,
    formattedUpcomingLittersDueDates
  ]);

  if (isLoading) {
    return (
      <div
        className={classnames('BreedAvailability flex gap-8', className)}
        style={{ padding: splideOptions?.padding }}
      >
        {/* Known from the card's own payload, so it does not wait on the
            request the skeletons stand in for. */}
        {breedDetail && (
          <Badge
            variant={breedDetail.variant}
            color={breedDetail.color}
            className="nowrap"
          >
            {breedDetail.description}
          </Badge>
        )}
        {makeSequence(BADGE_SKELETON_WIDTHS.length).map(index => (
          <SkeletonBlock
            key={index}
            className="br-pill"
            width={BADGE_SKELETON_WIDTHS[index]}
            height={BADGE_SKELETON_HEIGHT}
          />
        ))}
      </div>
    );
  }

  if (isEmpty(details)) return null;

  return (
    <Splide
      ref={splideRef}
      options={{
        arrows: false,
        pagination: false,
        autoWidth: true,
        snap: true,
        wheel: true,
        gap: 8,
        drag: true,
        keyboard: true,
        releaseWheel: true,
        ...splideOptions
      }}
      className={classnames('BreedAvailability', className)}
    >
      {details.map(({ color, variant, description }, index) => (
        <SplideSlide key={index}>
          <Badge color={color} variant={variant} className="nowrap">
            {description}
          </Badge>
        </SplideSlide>
      ))}
    </Splide>
  );
}

BreedAvailability.propTypes = {
  className: PropTypes.string,
  breedName: PropTypes.string,
  availablePuppyCount: PropTypes.number.isRequired,
  availableAdultDogsCount: PropTypes.number.isRequired,
  formattedUpcomingLittersDueDates: PropTypes.arrayOf(PropTypes.string),
  // eslint-disable-next-line react/forbid-prop-types
  splideOptions: PropTypes.object,
  isLoading: PropTypes.bool
};

BreedAvailability.defaultProps = {
  className: null,
  breedName: null,
  splideOptions: null,
  formattedUpcomingLittersDueDates: [],
  isLoading: false
};
